import {
  Badge,
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Spinner,
  Switch,
  tokens,
} from "@fluentui/react-components";
import {
  BarcodeScannerRegular,
  CheckmarkCircleRegular,
  TagMultipleRegular,
  WarningRegular,
} from "@fluentui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { catalogue, type SaveSerialFormat } from "../api/catalogue";
import { ApiError } from "../api/client";
import { baeTypography } from "../theme/baeTheme";
import { Page, PageHeader } from "../ui/PageHeader";
import { Card, CardHeader } from "../ui/Surface";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "start",
  },
  // The rendered serial, big enough to read across a desk. This is the whole point of the screen:
  // what the pattern actually produces.
  sample: {
    display: "grid",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    minWidth: 0,
  },
  serial: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "1.5rem",
    fontWeight: 500,
    letterSpacing: "0.02em",
    overflowWrap: "anywhere",
    [PHONE]: { fontSize: "1.15rem" },
  },
  scan: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "13px",
    color: tokens.colorNeutralForeground3,
    overflowWrap: "anywhere",
  },
  tokens: { display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalXS },
  // A button, because clicking one appends it to the pattern.
  token: {
    display: "grid",
    gap: "1px",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    ":hover": { border: `1px solid ${tokens.colorCompoundBrandStroke}` },
  },
  tokenCode: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "12px", fontWeight: 500 },
  muted: { color: tokens.colorNeutralForeground3 },
  width: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  mono: { fontFamily: baeTypography.fontFamilyMonospace },
});

const PRESETS: { label: string; hint: string; pattern: string }[] = [
  {
    label: "Store, month, number",
    hint: "LKO-2609-000123 — reads as where and when",
    pattern: "{WH}-{YY}{MM}-{SEQ:6}",
  },
  {
    label: "Prefix and number",
    hint: "FIL-000123 — shortest, and the same everywhere",
    pattern: "{PREFIX}-{SEQ:6}",
  },
  {
    label: "Store, full date, number",
    hint: "LKO-260901-000123 — the exact day it arrived",
    pattern: "{WH}-{YY}{MM}{DD}-{SEQ:6}",
  },
  {
    label: "Just a number",
    hint: "000123 — nothing to read, shortest barcode",
    pattern: "{SEQ:6}",
  },
];

/**
 * The shape of this business's serial numbers.
 *
 * The pattern language has been in the domain from the start; what was missing was any way for an
 * owner to reach it, so every tenant silently got the default. That default reads well — "Lucknow,
 * September 2026, unit 123" — but it is a decision, and it is theirs to make.
 *
 * The reason this is a screen and not a config value is the width warning. Every character costs
 * about 2.5mm of barcode on a 50mm label, so a pattern that reads beautifully can produce a code
 * no handheld scanner resolves — and nothing about the pattern shows that. So the API measures it
 * and this screen says so while there is still time to change it.
 */
export function SerialFormatPage() {
  const styles = useStyles();

  const [draft, setDraft] = useState<SaveSerialFormat | null>(null);
  const [saved, setSaved] = useState(false);

  const format = useQuery({ queryKey: ["serial-format"], queryFn: catalogue.serialFormat });

  useEffect(() => {
    if (!format.data || draft !== null) return;

    setDraft({
      pattern: format.data.pattern,
      prefix: format.data.prefix,
      appendCheckCharacter: format.data.appendCheckCharacter,
      checkSeparator: format.data.checkSeparator,
    });
  }, [format.data, draft]);

  // Previewed by the API, not rendered here. The token language and the barcode width are business
  // rules, and a second implementation in the browser would be a second implementation to keep
  // right (ADR-0009).
  const preview = useQuery({
    queryKey: ["serial-preview", draft],
    queryFn: () => catalogue.previewSerialFormat(draft as SaveSerialFormat),
    enabled: draft !== null && draft.pattern.trim() !== "",
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => catalogue.saveSerialFormat(draft as SaveSerialFormat),
    onSuccess: () => setSaved(true),
  });

  if (format.isLoading || !draft) {
    return <Spinner label="Loading the serial format" />;
  }

  const change = (next: Partial<SaveSerialFormat>) => {
    setSaved(false);
    setDraft({ ...draft, ...next });
  };

  const fits = preview.data?.fitsLabel ?? true;
  const patternError = preview.error;

  return (
    <Page width="medium">
      <PageHeader
        title="Serials & labels"
        subtitle="The code printed on every individually labelled piece. Change it here and new labels follow; anything already printed keeps the code it was printed with."
      />

      {save.error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {save.error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {saved && !save.isPending && (
        <MessageBar intent="success">
          <MessageBarBody>
            Saved. The next piece labelled will be{" "}
            <span className={styles.mono}>{format.data?.preview}</span>.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <CardHeader
          icon={<TagMultipleRegular />}
          title="What a serial looks like"
          hint="A live sample, using your busiest store and the next number in the counter."
        />

        <div className={styles.sample}>
          {preview.isFetching ? (
            <Spinner size="tiny" />
          ) : patternError instanceof ApiError ? (
            <Caption1 className={styles.muted}>{patternError.message}</Caption1>
          ) : (
            <>
              <span className={styles.serial}>{preview.data?.preview ?? "—"}</span>
              <span className={styles.scan}>
                barcode carries {preview.data?.scanPreview ?? "—"}
              </span>
            </>
          )}
        </div>

        {preview.data && (
          <div className={styles.width}>
            {fits ? (
              <Badge appearance="tint" color="success" icon={<CheckmarkCircleRegular />}>
                {preview.data.barcodeWidthMm}mm of barcode — fits a 50mm label
              </Badge>
            ) : (
              <Badge appearance="filled" color="danger" icon={<WarningRegular />}>
                {preview.data.barcodeWidthMm}mm of barcode — too wide for a 50mm label
              </Badge>
            )}
          </div>
        )}

        {!fits && preview.data?.warning && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>This would print and then not scan</MessageBarTitle>
              {preview.data.warning}
            </MessageBarBody>
          </MessageBar>
        )}
      </Card>

      <Card>
        <CardHeader
          icon={<BarcodeScannerRegular />}
          title="The pattern"
          hint="Tap a piece to add it. A running number is required, or every piece would get the same code."
        />

        <Field
          label="Pattern"
          required
          hint="Anything outside the braces is printed as you type it."
        >
          <Input
            value={draft.pattern}
            className={styles.mono}
            onChange={(_, d) => change({ pattern: d.value })}
          />
        </Field>

        <div className={styles.tokens}>
          {(format.data?.tokens ?? []).map((token) => (
            <button
              key={token.token}
              type="button"
              className={styles.token}
              onClick={() => change({ pattern: draft.pattern + token.token })}
            >
              <span className={styles.tokenCode}>{token.token}</span>
              <Caption1 className={styles.muted}>{token.name}</Caption1>
            </button>
          ))}
        </div>

        <Caption1 className={styles.muted}>Or start from one of these:</Caption1>

        <div className={styles.tokens}>
          {PRESETS.map((preset) => (
            <button
              key={preset.pattern}
              type="button"
              className={styles.token}
              onClick={() => change({ pattern: preset.pattern })}
            >
              <span>{preset.label}</span>
              <Caption1 className={styles.muted}>{preset.hint}</Caption1>
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Details" hint="The prefix only appears if your pattern uses it." />

        <div className={styles.pair}>
          <Field label="Your prefix" hint="What {PREFIX} prints. Short — it is scanned.">
            <Input
              value={draft.prefix ?? ""}
              placeholder="FIL"
              onChange={(_, d) => change({ prefix: d.value.toUpperCase() || null })}
            />
          </Field>

          <Field
            label="Separator before the check character"
            hint="One character, or leave empty to run it on."
          >
            <Input
              value={draft.checkSeparator}
              maxLength={1}
              disabled={!draft.appendCheckCharacter}
              onChange={(_, d) => change({ checkSeparator: d.value })}
            />
          </Field>
        </div>

        <Switch
          label="Add a check character"
          checked={draft.appendCheckCharacter}
          onChange={(_, d) => change({ appendCheckCharacter: d.checked })}
        />
        <Caption1 className={styles.muted}>
          One extra character that catches a serial typed wrong — during a stock count, or on a
          returns form. Worth the width unless the label is already tight.
        </Caption1>
      </Card>

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="large"
          disabled={save.isPending || !fits || draft.pattern.trim() === ""}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <Spinner size="tiny" /> : "Save format"}
        </Button>
        <Button
          appearance="subtle"
          size="large"
          onClick={() => {
            setSaved(false);
            setDraft({
              pattern: format.data?.pattern ?? "",
              prefix: format.data?.prefix ?? null,
              appendCheckCharacter: format.data?.appendCheckCharacter ?? true,
              checkSeparator: format.data?.checkSeparator ?? "-",
            });
          }}
        >
          Reset
        </Button>
      </div>

      <Caption1 className={styles.muted}>
        The counter is at {format.data?.nextSequence ?? 1}. Changing the pattern does not reset it,
        so no two pieces can ever end up with the same serial.
      </Caption1>
    </Page>
  );
}
